'use client';

import type { ComponentProps } from 'react';
import { useRef } from 'react';
import { FaSpinner } from 'react-icons/fa';
import { CSSTransition } from 'react-transition-group';

import styles from './loading-scrim.component.module.scss';

type Props = Pick<ComponentProps<'div'>, 'children' | 'className' | 'style'> & {
  coverall?: boolean;
  isLoading?: boolean;
};

/**
 * ローディング中のスクリムとインジケータを表示する。
 *
 * coverall を指定した場合は全画面を透過無しの白地でカバーする。
 * coverall を指定しない場合は children の領域を半透明の白地でカバーする。
 *
 * 全画面をカバーする例:
 *   <LoadingScrim coverall isLoading={state.isLoading} />
 *
 * 指定領域をカバーする例:
 *    <LoadingScrim isLoading={state.isLoading}>
 *      <p>some children contents</p>
 *    </LoadingScrim>
 *
 * @todo FaSpinnerにroleを付与する等してtestidなしでテストできるようにしたい
 */
export const LoadingScrim = ({
  children,
  className,
  coverall,
  isLoading,
  style,
}: Props) => {
  const nodeRef = useRef(null);

  return (
    <div
      aria-atomic="true"
      aria-live={isLoading ? 'assertive' : 'off'} // Loadingが非表示の時は `off` を指定して、childrenの変更をLoadingScrimで読み上げないようにする
      className={[styles.loadingScrim, className].filter(Boolean).join(' ')}
      data-testid="loading-scrim"
      style={{ ...style, position: isLoading ? 'relative' : 'static' }}
    >
      <CSSTransition
        exit
        unmountOnExit
        classNames="LoadingScrim__fade"
        in={isLoading}
        nodeRef={nodeRef}
        timeout={300}
      >
        <div
          className={
            coverall
              ? 'LoadingScrim__cover LoadingScrim__cover--coverall'
              : 'LoadingScrim__cover'
          }
          ref={nodeRef}
        >
          <FaSpinner
            aria-label="読み込み中"
            className="LoadingScrim__spinner"
          />
        </div>
      </CSSTransition>
      {children}
    </div>
  );
};
